import React, { memo, useState } from 'react';
import {
  Box, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, Tooltip, Typography, alpha, useTheme,
} from '@mui/material';
import { DoNotDisturbAltOutlined, EditOutlined, HistoryOutlined, LockOpenOutlined, LockOutlined, MoreVert, RestartAlt } from '@mui/icons-material';
import { format } from 'date-fns';
import { SubjectStatus } from '../../../types';
import { StudentExam, StudentPaper } from '../../../types/examination';
import { ToneChip } from '../../common/StatusChip';
import OutcomeChip from '../../common/OutcomeChip';
import { PAPER_STATUS } from '../marks/paperStatus';

export type ExamAction = 'edit' | 'notAppeared' | 'clear' | 'unlock';

interface Props {
  exam: StudentExam;
  canEdit: boolean;
  canUnlock: boolean;
  gradeColor: (percentage: number | null) => string;
  onAction: (action: ExamAction, exam: StudentExam) => void;
  onHistory: (exam: StudentExam, paper: StudentPaper) => void;
}

const dateRange = (a: string, b: string) => {
  const s = new Date(a); const e = new Date(b);
  return format(s, 'd MMM yyyy') === format(e, 'd MMM yyyy') ? format(s, 'd MMM yyyy') : `${format(s, 'd MMM')} – ${format(e, 'd MMM yyyy')}`;
};

/** One exam on a student's results: each paper as it stands, the overall, and what may be done with it. */
const ExamResultCard: React.FC<Props> = ({ exam, canEdit, canUnlock, gradeColor, onAction, onHistory }) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const locked = exam.papers.filter(p => p.isLocked).length;
  const recorded = exam.papers.filter(p => p.status !== SubjectStatus.PENDING).length;
  const open = exam.papers.length - locked;
  const act = (a: ExamAction) => { setAnchor(null); onAction(a, exam); };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2, py: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.03), borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={700}>{exam.examName}</Typography>
            {!exam.countsInReports && <ToneChip tone="neutral" label="Not in reports" />}
            {locked > 0 && <ToneChip tone="success" icon={LockOutlined} label={locked === exam.papers.length ? 'Submitted' : `${locked} submitted`} />}
          </Stack>
          <Typography variant="caption" color="text.secondary">{dateRange(exam.startDate, exam.endDate)} · {recorded} of {exam.papers.length} papers marked</Typography>
        </Box>
        {exam.overallPercentage != null ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {exam.totalObtained}/{exam.totalPossible} · {exam.overallPercentage.toFixed(1)}%
            </Typography>
            <Chip label={exam.overallGrade || '—'} size="small" sx={{ bgcolor: gradeColor(exam.overallPercentage), color: 'common.white', fontWeight: 700, minWidth: 34 }} />
            {exam.outcome && <OutcomeChip outcome={exam.outcome} failedSubjects={exam.failedSubjects} />}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {!recorded ? 'No marks yet' : exam.papers.every(p => p.status === SubjectStatus.NOT_APPEARED) ? 'Not appeared' : 'Not scored'}
          </Typography>
        )}
        {(canEdit || (canUnlock && locked > 0)) && (
          <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} aria-label={`Actions for ${exam.examName}`}><MoreVert fontSize="small" /></IconButton>
        )}
      </Stack>

      <Table size="small" sx={{ '& td, & th': { py: 0.9 } }}>
        <TableHead>
          <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'text.secondary' } }}>
            <TableCell>Subject</TableCell>
            <TableCell align="right">Marks</TableCell>
            <TableCell align="right">%</TableCell>
            <TableCell align="center">Grade</TableCell>
            <TableCell width={72} />
          </TableRow>
        </TableHead>
        <TableBody>
          {exam.papers.map(p => {
            const scored = p.status === SubjectStatus.PRESENT;
            return (
              <TableRow key={p.subjectId} hover sx={{ '&:hover .paper-history': { opacity: 1 } }}>
                <TableCell><Typography variant="body2" fontWeight={500}>{p.subjectName}</Typography></TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {scored
                    ? <><b>{p.obtainedMarks}</b><Typography component="span" variant="caption" color="text.secondary"> / {p.maxMarks}</Typography></>
                    : <ToneChip tone={PAPER_STATUS[p.status].tone} label={PAPER_STATUS[p.status].label} />}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{scored ? `${p.percentage.toFixed(1)}%` : '—'}</TableCell>
                <TableCell align="center">
                  {scored && p.grade ? <Chip label={p.grade} size="small" sx={{ bgcolor: gradeColor(p.percentage), color: 'common.white', fontWeight: 700, minWidth: 34, height: 22 }} /> : '—'}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {p.isLocked && (
                    <Tooltip title={`Submitted${p.lockedBy ? ` by ${p.lockedBy}` : ''}${p.lockedAt ? ` on ${format(new Date(p.lockedAt), 'd MMM yyyy')}` : ''}`}>
                      <LockOutlined sx={{ fontSize: 15, color: 'text.disabled', verticalAlign: 'middle' }} />
                    </Tooltip>
                  )}
                  <Tooltip title="History">
                    <IconButton size="small" className="paper-history" onClick={() => onHistory(exam, p)} sx={{ opacity: { xs: 1, md: 0.35 } }} aria-label={`History of ${p.subjectName}`}>
                      <HistoryOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} PaperProps={{ sx: { minWidth: 230 } }}>
        {canEdit && (
          <MenuItem dense disabled={!open} onClick={() => act('edit')}>
            <ListItemIcon><EditOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primary="Edit marks" secondary={!open ? 'Every paper is submitted' : undefined} />
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem dense disabled={!open} onClick={() => act('notAppeared')}>
            <ListItemIcon><DoNotDisturbAltOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primary="Not appeared for this exam" />
          </MenuItem>
        )}
        {canEdit && (
          <MenuItem dense disabled={!recorded || recorded === locked} onClick={() => act('clear')}>
            <ListItemIcon><RestartAlt fontSize="small" /></ListItemIcon>
            <ListItemText primary="Clear marks" />
          </MenuItem>
        )}
        {canUnlock && locked > 0 && (
          <MenuItem dense onClick={() => act('unlock')}>
            <ListItemIcon><LockOpenOutlined fontSize="small" /></ListItemIcon>
            <ListItemText primary="Unlock submitted marks" />
          </MenuItem>
        )}
      </Menu>
    </Paper>
  );
};

export default memo(ExamResultCard);
